/**
 * MotionInput — pure ring-buffer motion-input recogniser.
 *
 * No Phaser import — fully unit-testable in node. The Phaser adapter (Player.ts)
 * calls push() each frame with the current directional state, then calls
 * tryConsumeQcf / tryConsumeDP / tryConsumeCharge on the frame SPECIAL is pressed.
 *
 * Supports:
 *   QCF   — quarter-circle-forward: DOWN (→ DOWN_FWD optional) → FWD
 *   DP    — dragon punch: FWD → DOWN → DOWN_FWD
 *   CHARGE — hold back ≥ CHARGE_HOLD_FRAMES, then forward
 */

/** 8-way directional stick position + neutral. */
export type Dir =
  | 'NEUTRAL'
  | 'UP'
  | 'DOWN'
  | 'LEFT'
  | 'RIGHT'
  | 'UP_LEFT'
  | 'UP_RIGHT'
  | 'DOWN_LEFT'
  | 'DOWN_RIGHT';

export interface BufferEntry {
  dir: Dir;
  /** Consecutive frames this direction was held. */
  frames: number;
}

/** Minimum frames the back direction must be held for a charge motion. */
export const CHARGE_HOLD_FRAMES = 40;

/** Max total elapsed frames allowed for a full QCF motion. */
export const QCF_WINDOW_FRAMES = 20;

/** Max total elapsed frames allowed for a full DP motion. */
export const DP_WINDOW_FRAMES = 20;

const BUFFER_CAP = 32;

/**
 * MotionInputBuffer — tracks directional history and recognises SF-style motions.
 *
 * Call push() once per game frame with the current directional stick value.
 * Call tryConsume* on the frame the attack button is pressed. Each tryConsume*
 * method returns true and clears the matched portion of the buffer on success,
 * or returns false and leaves the buffer untouched on failure.
 */
export class MotionInputBuffer {
  private buf: BufferEntry[] = [];

  /**
   * Record the current directional state for this frame.
   * Consecutive identical directions are accumulated into one entry.
   */
  push(dir: Dir): void {
    const last = this.buf[this.buf.length - 1];
    if (last && last.dir === dir) {
      last.frames++;
      return;
    }
    this.buf.push({ dir, frames: 1 });
    if (this.buf.length > BUFFER_CAP) this.buf.shift();
  }

  /**
   * QCF: DOWN (→ DOWN_FWD optional) → FWD, within QCF_WINDOW_FRAMES total.
   * Consumes the matched portion on success.
   */
  tryConsumeQcf(facingRight: boolean): boolean {
    const fwd: Dir = facingRight ? 'RIGHT' : 'LEFT';
    const df: Dir = facingRight ? 'DOWN_RIGHT' : 'DOWN_LEFT';
    const len = this.buf.length;
    if (len < 2) return false;

    // Tail must be FWD.
    if (this.buf[len - 1].dir !== fwd) return false;

    // Walk back through optional diagonal.
    let i = len - 2;
    if (i >= 0 && this.buf[i].dir === df) i--;

    // Must find DOWN.
    if (i < 0 || this.buf[i].dir !== 'DOWN') return false;

    // Verify the entire motion fits within the window.
    const window = this.buf.slice(i);
    if (totalFrames(window) > QCF_WINDOW_FRAMES) return false;

    this.buf.splice(i);
    return true;
  }

  /**
   * DP (dragon punch): FWD → DOWN → DOWN_FWD, within DP_WINDOW_FRAMES total.
   * Consumes the matched portion on success.
   */
  tryConsumeDP(facingRight: boolean): boolean {
    const fwd: Dir = facingRight ? 'RIGHT' : 'LEFT';
    const df: Dir = facingRight ? 'DOWN_RIGHT' : 'DOWN_LEFT';
    const len = this.buf.length;
    if (len < 3) return false;

    if (this.buf[len - 1].dir !== df) return false;
    if (this.buf[len - 2].dir !== 'DOWN') return false;
    if (this.buf[len - 3].dir !== fwd) return false;

    const window = this.buf.slice(len - 3);
    if (totalFrames(window) > DP_WINDOW_FRAMES) return false;

    this.buf.splice(len - 3);
    return true;
  }

  /**
   * Charge: back held ≥ CHARGE_HOLD_FRAMES → forward.
   * Consumes the matched portion on success.
   */
  tryConsumeCharge(facingRight: boolean): boolean {
    const back: Dir = facingRight ? 'LEFT' : 'RIGHT';
    const fwd: Dir = facingRight ? 'RIGHT' : 'LEFT';
    const len = this.buf.length;
    if (len < 2) return false;

    if (this.buf[len - 1].dir !== fwd) return false;
    if (this.buf[len - 2].dir !== back) return false;
    if (this.buf[len - 2].frames < CHARGE_HOLD_FRAMES) return false;

    this.buf.splice(len - 2);
    return true;
  }

  /** Clear all buffered entries. */
  clear(): void {
    this.buf = [];
  }

  /** Read-only view of the buffer (for testing). */
  getEntries(): readonly BufferEntry[] {
    return this.buf;
  }
}

/**
 * Convert discrete cardinal-direction booleans to a Dir value.
 * Maps the four digital inputs (left/right/up/down) to an 8-way direction.
 */
export function dirsToDir(left: boolean, right: boolean, up: boolean, down: boolean): Dir {
  const h = right ? 1 : left ? -1 : 0;
  const v = down ? 1 : up ? -1 : 0;
  if (h === 0 && v === 0) return 'NEUTRAL';
  if (h === 0 && v === -1) return 'UP';
  if (h === 0 && v === 1) return 'DOWN';
  if (h === -1 && v === 0) return 'LEFT';
  if (h === 1 && v === 0) return 'RIGHT';
  if (h === -1 && v === -1) return 'UP_LEFT';
  if (h === 1 && v === -1) return 'UP_RIGHT';
  if (h === -1 && v === 1) return 'DOWN_LEFT';
  return 'DOWN_RIGHT'; // h === 1 && v === 1
}

function totalFrames(entries: BufferEntry[]): number {
  return entries.reduce((sum, e) => sum + e.frames, 0);
}
